import type { ReactNode } from 'react';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { useOrganization } from '@/hooks/auth/useOrganization';
import type { UseOrganizationResult } from '@/hooks/auth/useOrganization';

type ActiveOrganization = NonNullable<UseOrganizationResult['activeOrganization']>;

interface RequireActiveOrganizationProps {
  /** Page title rendered in the no-active-organization fallback. */
  title: string;
  /** Renders the page body once an active organization is available. */
  children: (org: ActiveOrganization) => ReactNode;
}

/**
 * Standard guard for org-scoped pages.
 *
 * Renders a `PageLayout` + `PageHeader` with "No active organization."
 * when there is no active membership. Otherwise calls `children(org)`
 * so the page body can read organization fields without re-checking
 * for `null`.
 */
export function RequireActiveOrganization({ title, children }: RequireActiveOrganizationProps) {
  const { activeOrganization } = useOrganization();
  if (!activeOrganization) {
    return (
      <PageLayout>
        <PageHeader title={title} description="No active organization." />
      </PageLayout>
    );
  }
  return <>{children(activeOrganization)}</>;
}
